<script setup lang="ts">

import DefaultVue from "@/views/layouts/Default.vue";
import Link from "@/views/components/Link.vue";
import { usePageContext } from "vike-vue/usePageContext";
import 'vue3-carousel/dist/carousel.css'
import { Carousel, Slide } from "vue3-carousel";
import { onMounted, Ref, ref, computed, watch, defineAsyncComponent, onBeforeUnmount, nextTick } from "vue";
import { searchWorkers } from "@/services/modules/WorkerService.ts";
import { Pro } from "@/models/Pro.ts";
import { formatTextWithStyles, getUserFromLocalStorage, isAuth, logout } from "@/utils/functions.ts";
import { navigate } from "vike/client/router";
import useChatStore from "@/stores/chat.ts";
import { storeToRefs } from 'pinia';
import { useBusinessRequestFormStore } from '@/pages/index/stores/business-request-form-store';
import { logNewEvent } from '@/services/modules/firebase';
import i18n from "@/localization/i18n";
import useAuthStore from "@/stores/auth";
import useRoseRequestStore from "@/stores/roseRequest";
import { useUserStore } from "@/stores/user";
import { useAlertsStore } from "@/stores/alerts";
import { getProfileInfo } from "@/services";
import { ErrorResponse } from "@/utils/enums";
import { IRoseRequestAddress, IUser } from "@/models/interfaces/interfaces";


import DynamicTitleInput from '@/pages/index/components/DynamicTitleInput.vue'
import CsBlog from "./components/CsBlog.vue";
import PublicLatestRequests from '@/pages/(public)/components/PublicLatestRequests.vue'

const ImageOff = defineAsyncComponent(() => import('vue-material-design-icons/ImageOff.vue'))

const Star = defineAsyncComponent(() => import('vue-material-design-icons/Star.vue'))
const CheckDecagram = defineAsyncComponent(() => import('vue-material-design-icons/CheckDecagram.vue'))
const Lock = defineAsyncComponent(() => import('vue-material-design-icons/Lock.vue'))
const ReceiptText = defineAsyncComponent(
  () => import('vue-material-design-icons/ReceiptText.vue')
)
const CommentCheckOutline = defineAsyncComponent(
  () => import('vue-material-design-icons/CommentCheckOutline.vue')
)
const ClipboardText = defineAsyncComponent(
  () => import('vue-material-design-icons/ClipboardText.vue')
)

const MapMarker = defineAsyncComponent(
  () => import('vue-material-design-icons/MapMarker.vue')
)

const ShieldCheck = defineAsyncComponent(
  () => import('vue-material-design-icons/ShieldCheck.vue')
)

const BusinessRequestForm = defineAsyncComponent(() =>
  import('./components/business-request-form/BusinessRequestForm.vue')
);
const AccountQuestion = defineAsyncComponent(
  () => import('vue-material-design-icons/AccountQuestion.vue')
)

const AccountHardHat = defineAsyncComponent(
  () => import('vue-material-design-icons/AccountHardHat.vue')
)

const ModalComponent = defineAsyncComponent(() =>
  import('@/views/components/ModalComponent.vue')
);

const OrangeCircleCheck = defineAsyncComponent(() =>
  import('@/pages/index/components/OrangeCircleCheck.vue')
);

const ImageUploadLimitModal = defineAsyncComponent(() =>
  import('@/views/components/ImageUploadLimitModal.vue')
);

const ImageUploadDimensionLimitModal = defineAsyncComponent(() =>
  import('@/views/components/ImageUploadDimensionLimitModal.vue')
);

const ReinitializePassword = defineAsyncComponent(() =>
  import('@/pages/index/components/business-request-form/ReinitializePassword.vue')
);

const ErrorComponent = defineAsyncComponent(() =>
  import('@/pages/index/components/business-request-form/ErrorComponent.vue')
);

const SavComponent = defineAsyncComponent(() =>
  import('@/views/components/SavComponent.vue')
);

const RoseAssistant = defineAsyncComponent(() =>
  import('@/views/components/rose/RoseAssistant.vue')
);


const businessRequestFormStore = useBusinessRequestFormStore()
const alerts = useAlertsStore()
const { is_modal_visible } = storeToRefs(businessRequestFormStore)

const auth = useAuthStore()

const pageContext = usePageContext()
const t = i18n.global.t
const VITE_ASSET_URL = import.meta.env.VITE_ASSET_URL
const isClient = ref(false)
onMounted(() => { isClient.value = true })

const workersList: Ref<Pro[]> = ref([])
const prosLoading = ref(true)
const errorLoadingPros = ref(false)

const chatStore = useChatStore()
const chatpath = ref('/cs/msg_list')
const modalbutton: Ref<any> = ref(null)
const closemodal: Ref<any> = ref(null)

let activeSlide = ref(0)
const slideItemsNumber = 8
const slideItemsPerPage = ref(4)

const hydrated = ref(false);

const checkScreenSize = () => {
  if (window.innerWidth <= 600) {
    slideItemsPerPage.value = 1
  } else if (window.innerWidth >= 600 && window.innerWidth <= 992) {
    slideItemsPerPage.value = 2
  } else if (window.innerWidth > 992) {
    slideItemsPerPage.value = 3
  }
}

const postRequest = () => {
  businessRequestFormStore.current_context = "presentation"
  logNewEvent("presta_request_pop_up_open")
  const requestPostButton = document.getElementById("requestPostButton")
  if (requestPostButton) {
    requestPostButton.click()
  }
}
// const hero_image_mobile = `url(${VITE_ASSET_URL}assets/public/assets/img/homepage/Photo_BtoB_mobile.webp)`
// const hero_image_desktop = `url(${VITE_ASSET_URL}assets/public/assets/img/homepage/Photo_BtoB_desktop.webp)`

let metaTag: any;
onMounted(() => {
  metaTag = document.createElement("meta");
  metaTag.name = "robots";
  metaTag.content = "index, follow";
  document.head.appendChild(metaTag);

  if (isAuth()) {
    const user = getUserFromLocalStorage()
    let userStore = useUserStore()

    if (user === null) {
      getProfileInfo()
        .then((response) => {
          let user_info: IUser = response.data

          sessionStorage.setItem('user', JSON.stringify(user_info))
          userStore.addUser(user_info)
          auth.setIsLoggedIn(true)
        })
        .catch((err: any) => {
          if (err.response?.data.code == ErrorResponse.USER_NOT_FOUND) {
            logout()
          }
        })
    } else {
      userStore.addUser(user)
      auth.setIsLoggedIn(true)
    }
  }

  checkScreenSize()

  window.addEventListener('resize', checkScreenSize);

  const config = {
    params: {
      page: pageContext.urlParsed.search.page ?? 1
    },
    timeout: 3000
  }

  const payload = {
    "platform_code": null,
    "keywords": [],
    "town": null,
    "department": null,
    "zipcode": null
  }

  prosLoading.value = true
  searchWorkers(payload, config)
    .then((response) => {
      workersList.value = response.data.items.map((item: any) => {
        return {
          ...item,
          address: {
            town: item.town,
            zipcode: item.zipcode
          }
        }
      })
      prosLoading.value = false
    })
    .catch(() => {
      // console.log("Error loading pros", err)
      prosLoading.value = false
      errorLoadingPros.value = true
    })

  hydrated.value = true

  if (pageContext.urlParsed.search.br === 'true') {
    const waitForModal = () => {
      if (document.getElementById('requestPost')) {
        postRequest()
      } else {
        requestAnimationFrame(waitForModal)
      }
    }
    nextTick(waitForModal)
  }
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', checkScreenSize);
  workersList.value = []
  cleanUp()
  if (metaTag && document.head.contains(metaTag)) {
    document.head.removeChild(metaTag);
  }
  closeModalBeforeLeaving()
});

function closeModalBeforeLeaving() {
  if (is_modal_visible.value) {
    is_modal_visible.value = false
    const dismissButton = document.getElementById('dismissRequestPostModal')
    if (dismissButton) {
      dismissButton.click()
    }
  }
}


function cleanUp() {
  workersList.value = []
}

async function onContact(artisan: Pro) {
  let conversation = {
    "conversation_id": "conversation_" + artisan.account_id,
    "pro": {
      "account_id": artisan.account_id,
      "first_name": artisan.first_name,
      "last_name": artisan.last_name,
      "phone": artisan.phone,
      "company_name": artisan.company_name,
      "logo": artisan.logo,
      'platform_code': artisan.platform_code,
      "is_active": artisan.is_active
    },
    "last_message": {}
  }

  chatStore.setContactArtisanConvo(conversation)

  if (isAuth()) {
    await navigate(chatpath.value)
  } else {
    modalbutton.value?.click()
  }
}

async function goToLogin() {
  closemodal.value?.click()
  sessionStorage.setItem('destination', chatpath.value)
  await navigate('/auth/login')
}

const formattedFeatureSectionHeading = (originalText: string) => {
  let formattedText = formatTextWithStyles(
    originalText,
    [
      { word: "A", styleClass: "text-orange" },
      { word: "Z", styleClass: "text-blue" },
    ]
  )

  formattedText = formattedText.replace(/topela/g, '<span class="text-blue">tope</span><span class="text-orange">la</span>');
  formattedText = formattedText + " " + `<span style="font-size: medium" class="ms-3 badge bg-light text-dark"><h3 class="free-text bg-light text-dark" style="font-size:medium;margin:0px;color: rgba(var(--bs-dark-rgb), var(--bs-text-opacity)) !important;">${t('w_txt_home_free_label')}</h3></span>`
  return formattedText
}

const activeTab = ref<'client' | 'pro'>('client')

</script>

<template>
  <h6 class="position-fixed top-0 start-0 end-0 text-center py-1"
    style="background-color:#49AFDA !important;color: white;z-index:1500">
    <span class="d-inline d-md-none">{{ t('w_txt_home_pro_heading') }}</span>
    <span class="d-none d-md-inline">{{ t('w_txt_home_pro_heading_2') }}</span>
    <span>&nbsp;<Link href="/pro/" target="_blank" class="text-decoration-underline text-white link-hover-blue"
        type="button">{{ t('w_txt_home_pro_heading_linkText') }}</Link></span>

  </h6>

  <div style="background-color: #f8f8f8;">
    <DefaultVue>
      <header id="home" class="position-relative" style="padding-top:140px;">
        <div class="partner-login-hotspot text-center py-2 fw-bold" role="link" tabindex="0"
          aria-label="Accès partenaire" @click="navigate('/partner/login/')"
          @keydown.enter="navigate('/partner/login/')" @keydown.space.prevent="navigate('/partner/login/')"></div>
        <div class="container">

          <div class="row g-4 align-items-center d-md-flex justify-content-center">

            <!-- TEXTE -->
            <div class="col-12 col-md-6 text-center text-md-start">

              <h1 class="fw-semibold mb-3" style="font-size:clamp(2.2rem, 4vw, 4rem); line-height:1.2;">
                {{ t('w_txt_home_header_title') }}
              </h1>

              <h3 class="fw-normal mb-3" style="font-size:clamp(1.2rem, 2vw, 1.6rem);">
                {{ t('w_txt_home_header_subtitle') }}
              </h3>

              <p class="text-muted mb-4" style="font-size:clamp(1rem, 1.5vw, 1.1rem);">
                {{ t('w_txt_home_header_paragraphe') }}
              </p>



            </div>

            <!-- IMAGE -->
            <div class="col-12 col-md-4 d-flex justify-content-center justify-content-md-end">

              <div class="w-100 d-flex justify-content-center" :style="{
                backgroundImage: `url(${VITE_ASSET_URL}assets/public/assets/img/topela_img_bg.png)`,
                backgroundSize: 'contain',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }">
                <img :src="VITE_ASSET_URL + 'assets/public/assets/img/pro_and_client.webp'" alt="Topela"
                  class="img-fluid d-none d-md-block" style="max-height:400px;" />
              </div>

            </div>

          </div>
        </div>
      </header>


      <section class="py-4 py-md-5 bg-dark-blue">
        <div class="container">

          <!-- ================= MOBILE TABS ================= -->
          <div class="d-flex d-md-none mb-4 justify-content-center gap-2">
            <button class="btn w-50" :class="activeTab === 'client'
              ? 'btn-warning text-dark'
              : 'btn-outline-warning'" @click="activeTab = 'client'">
              {{ t('w_txt_home_header_particular') }}
            </button>

            <button class="btn w-50" :class="activeTab === 'pro'
              ? 'btn-info text-white'
              : 'btn-outline-info'" @click="activeTab = 'pro'">
              {{ t('w_txt_home_header_professionnal') }}
            </button>
          </div>

          <!-- ================= MOBILE CONTENT ================= -->
          <div class="d-md-none">

            <!-- PARTICULIER -->
            <div v-if="activeTab === 'client'" class="card p-4 text-center shadow-sm">
              <h3 class="mb-3 text-warning d-flex justify-content-center align-items-center gap-2">
                <AccountQuestion :size="32" />
                {{ t('w_txt_home_header_professionnal_search') }}
              </h3>

              <p class="mb-4">
                {{ t('w_txt_home_header_professionnal_search_paragraph') }}
              </p>

              <div class="mb-3">
                <DynamicTitleInput />
              </div>

              <a class="text-decoration-underline" href="/annuaire/search/">
                {{ t('w_txt_home_header_professionnal_search_cta') }}
              </a>
            </div>

            <!-- PROFESSIONNEL -->
            <div v-if="activeTab === 'pro'" class="card p-4 text-center shadow-sm">
              <h3 class="mb-3 text-info d-flex justify-content-center align-items-center gap-2">
                <AccountHardHat :size="32" />
                {{ t('w_txt_home_header_professionnal_lead') }}
              </h3>

              <p class="mb-4">
                {{ t('w_txt_home_header_professionnal_lead_paragraph') }}
              </p>

              <a class="btn btn-info w-100 py-2 mb-3" href="/ps/home/" target="_blank">
                {{ t('w_txt_home_header_professionnal_lead_cta') }}
              </a>

              <a class="text-info text-decoration-underline" href="/pro/" target="_blank">
                {{ t('w_txt_home_header_professionnal_lead_cta_2') }}

              </a>
            </div>

          </div>

          <!-- ================= DESKTOP CONTENT ================= -->
          <div class="row g-4 justify-content-center d-none d-md-flex">

            <!-- PARTICULIER -->
            <div class="col-md-6 col-lg-5">
              <div class="card h-100 p-4 text-start shadow-sm">
                <h3 class="mb-3 text-warning d-flex align-items-center gap-2">
                  <AccountQuestion :size="32" />
                  {{ t('w_txt_home_header_professionnal_search') }}
                </h3>

                <p class="mb-4">
                  {{ t('w_txt_home_header_professionnal_search_paragraph') }}
                </p>

                <div class="mb-3">
                  <DynamicTitleInput />
                </div>

                <a class="text-decoration-underline text-center" href="/annuaire/search/">
                  {{ t('w_txt_home_header_professionnal_search_cta') }}
                </a>
              </div>
            </div>

            <!-- PROFESSIONNEL -->
            <div class="col-md-6 col-lg-5">
              <div class="card h-100 p-4 text-start shadow-sm">
                <h3 class="mb-3 text-info d-flex align-items-center gap-2">
                  <AccountHardHat :size="32" />
                  {{ t('w_txt_home_header_professionnal_lead') }}
                </h3>

                <p class="mb-4">
                  {{ t('w_txt_home_header_professionnal_lead_paragraph') }}
                </p>

                <div class="mb-3"><a class="btn btn-info w-100 py-3 my-4" href="/ps/home/" target="_blank">
                    {{ t('w_txt_home_header_professionnal_lead_cta') }}
                  </a>
                </div>
                <a class="text-info text-decoration-underline text-center" href="/pro/" target="_blank">
                  {{ t('w_txt_home_header_professionnal_lead_cta_2') }}
                </a>
              </div>
            </div>

          </div>

        </div>
      </section>

      <PublicLatestRequests />

      <section class="py-5 ">
        <div class="container text-center">
          <h2 class="fw-semibold mb-5">
            {{ t('w_txt_home_how_topela_works_title') }}
          </h2>

          <div class=" row g-4 justify-content-center my-5">

            <!-- STEP 1 -->
            <div class="col-md-4 p-4">
              <div class="card h-100 position-relative text-center pt-5 shadow-sm">

                <span class="badge rounded-circle bg-warning text-white
                       position-absolute top-0 start-50 translate-middle step-badge">
                  1
                </span>

                <div class="card-body">
                  <h5 class="fw-semibold mb-2">
                    {{ t('w_txt_home_how_topela_works_step1_title') }}
                  </h5>
                  <p class="text-muted mb-0">

                    {{ t('w_txt_home_how_topela_works_step1_paragraph') }}
                  </p>
                </div>

              </div>
            </div>

            <!-- STEP 2 -->
            <div class="col-md-4 p-4">
              <div class="card h-100 position-relative text-center pt-5 shadow-sm">

                <span class="badge rounded-circle bg-warning text-white
                       position-absolute top-0 start-50 translate-middle step-badge">
                  2
                </span>

                <div class="card-body">
                  <h5 class="fw-semibold mb-2">
                    {{ t('w_txt_home_how_topela_works_step2_title') }}
                  </h5>
                  <p class="text-muted mb-0">
                    {{ t('w_txt_home_how_topela_works_step2_paragraph') }}
                  </p>
                </div>

              </div>
            </div>

            <!-- STEP 3 -->
            <div class="col-md-4 p-4">
              <div class="card h-100 position-relative text-center pt-5 shadow-sm">

                <span class="badge rounded-circle bg-warning text-white
                       position-absolute top-0 start-50 translate-middle step-badge">
                  3
                </span>

                <div class="card-body">
                  <h5 class="fw-semibold mb-2">
                    {{ t('w_txt_home_how_topela_works_step3_title') }}
                  </h5>
                  <p class="text-muted mb-0">
                    {{ t('w_txt_home_how_topela_works_step3_paragraph') }}
                  </p>
                </div>

              </div>
            </div>

          </div>

          <!-- CTA -->
          <div class="d-flex justify-content-center mt-4">
            <a class="btn btn-primary rounded  btn-height btn-text-visibility px-5 " @click="postRequest">
              {{ t('w_txt_home_how_topela_works_cta') }}
            </a>
          </div>

        </div>
      </section>

      <section class="py-5 bg-dark-blue">
        <div class="container text-center ">

          <!-- Titre -->
          <h2 class="fw-semibold mb-2 text-white">
            {{ t('w_txt_home_header_professionnal_title') }}
          </h2>
          <p class=" mb-5 text-white">
            {{ t('w_txt_home_header_professionnal_subtitle') }}
          </p>

          <!-- Cartes fonctionnalités -->
          <div class="row g-4 justify-content-center mb-5">

            <div class="col-md-3">
              <div class="card p-4 h-100 ">
                <div class="pro-feature-icon">
                  <ReceiptText :size="40" />
                </div>
                <h5 class="fw-semibold mb-2">
                  {{ t('w_txt_home_professional_feature_1_title') }}
                </h5>
                <p class="small text-muted mb-0">
                  {{ t('w_txt_home_professional_feature_1_paragraph') }}
                </p>
              </div>
            </div>

            <div class="col-md-3">
              <div class="card p-4 h-100">
                <div class="pro-feature-icon">
                  <ClipboardText :size="40" />
                </div>
                <h5 class="fw-semibold mb-2">
                  {{ t('w_txt_home_professional_feature_2_title') }}
                </h5>
                <p class="small text-muted mb-0">
                  {{ t('w_txt_home_professional_feature_2_paragraph') }}
                </p>
              </div>
            </div>

            <div class="col-md-3">
              <div class="card p-4 h-100">
                <div class="pro-feature-icon">
                  <MapMarker :size="40" />
                </div>
                <h5 class="fw-semibold mb-2">
                  {{ t('w_txt_home_professional_feature_3_title') }}
                </h5>
                <p class="small text-muted mb-0">
                  {{ t('w_txt_home_professional_feature_3_paragraph') }}
                </p>
              </div>
            </div>

            <div class="col-md-3">
              <div class="card p-4 h-100">
                <div class="pro-feature-icon">
                  <ShieldCheck :size="40" />
                </div>
                <h5 class="fw-semibold mb-2">
                  {{ t('w_txt_home_professional_feature_4_title') }}
                </h5>
                <p class="small text-muted mb-0">
                  {{ t('w_txt_home_professional_feature_4_paragraph') }}
                </p>
              </div>
            </div>

          </div>

          <!-- CTA -->
          <div class="mt-5">
            <a class="btn btn-info rounded px-5 py-2 mb-4  btn-text-visibility" href="/ps/home/" target="_blank">
              {{ t('w_txt_home_professional_feature_cta') }}
            </a>
            <div>
              <a href="/pro/" class="text-info text-decoration-underline" target="_blank">
                {{ t('w_txt_home_professional_feature_cta_2') }}
              </a>
            </div>
          </div>

        </div>
      </section>

      <section class="py-5 bg-white">
        <div class="container text-center">

          <!-- Titre -->
          <h2 class="fw-semibold mb-5">
            {{ t('w_txt_home_partners_title') }}
          </h2>

          <!-- Stats -->
          <div class="row justify-content-center g-4">

            <!-- Utilisateurs actifs -->
            <div class="col-md-4">
              <div class="d-flex flex-column align-items-center border rounded-3 p-4 h-100">
                <div class="fw-bold display-3 ">
                  {{ t('w_txt_home_stats_users') }}
                </div>
                <div class="fw-semibold fs-1 ">
                  {{ t('w_txt_home_stats_users_label') }}
                </div>
                <p>
                  {{ t('w_txt_home_stats_users_paragraph') }}
                </p>
              </div>
            </div>

            <!-- Demandes gérées -->
            <div class="col-md-4">
              <div class="d-flex flex-column align-items-center border rounded-3 p-4 h-100">
                <div class="fw-bold display-3">
                  {{ t('w_txt_home_stats_requests') }}
                </div>
                <div class="fw-semibold fs-1">
                  {{ t('w_txt_home_stats_requests_label') }}
                </div>
                <p>
                  {{ t('w_txt_home_stats_requests_paragraph') }}
                </p>
              </div>
            </div>

            <!-- Note moyenne -->
            <div class="col-md-4">
              <div class="d-flex flex-column align-items-center border rounded-3 p-4 h-100">

                <!-- Icône -->
                <div class=" d-flex flex-row align-items-center ">
                  <div class="d-flex justify-content-center align-items-center
                    me-4 ">
                    <CommentCheckOutline :size="50" />
                  </div>
                  <div class="fw-bold display-3">
                    {{ t('w_txt_home_stats_rating') }}
                  </div>
                </div>

                <div class="fw-semibold fs-1">
                  {{ t('w_txt_home_stats_rating_label') }}

                </div>
                <p>{{ t('w_txt_home_stats_rating_paragraph') }}</p>
              </div>
            </div>

          </div>

        </div>

        <div class="col-lg-12 mt-5">
          <div class="d-flex justify-content-center flex-wrap align-items-center gap-4 partners-logos">

            <img class="partner-logo"
              :src="VITE_ASSET_URL + 'assets/public/assets/img/partners/reseau entreprendre.webp'"
              alt="Réseau Entreprendre" />

            <img class="partner-logo" :src="VITE_ASSET_URL + 'assets/public/assets/img/partners/MDE_vallee_sud.webp'"
              alt="MDE Vallée Sud" />

            <img class="partner-logo" :src="VITE_ASSET_URL + 'assets/public/assets/img/partners/CCI.webp'" alt="CCI" />

            <img class="partner-logo" :src="VITE_ASSET_URL + 'assets/public/assets/img/partners/Logo_Plessis.webp'"
              alt="Le Plessis" />
            <a href="https://www.pennylane.com/fr/integrations/topela" target="_blank">
              <img class="partner-logo" :src="VITE_ASSET_URL + 'assets/public/assets/img/partners/Logo_Pennylane.webp'"
                alt="Pennylane" />
            </a>
            <a href="https://www.leroymerlin.fr/" target="_blank">
              <img class="partner-logo" :src="VITE_ASSET_URL + 'assets/public/assets/img/partners/logo_lm_massy.webp'"
                alt="Leroy Merlin Massy" />
            </a>
          </div>
        </div>






      </section>


      <section class="list-of-pros pt-0 mt-0 bg-dark-blue">
        <div class="container ">
          <nav class="not-visible-content">
            <a href="/">
              {{ t('w_txt_home_consult_pro_directory') }}
            </a>
          </nav>
          <h2 class="text-center mt-md-5 mb-5 fw-normal h-style h2-style use-poppins text-white">{{
            t('w_txt_home_pro_section_heading')
          }}</h2>
          <div v-if="!prosLoading">
            <div v-if="workersList.length > 0" class="row">
              <Carousel :autoplay="isClient ? 3000 : 0" :itemsToShow="slideItemsPerPage" :transition="700" :wrap-around="true"
                class="custom-carousel" snapAlign="start" v-model="activeSlide">
                <Slide v-for="slide in slideItemsNumber" :key="slide" class="col-md-3">
                  <div class="carousel__item w-100">
                    <div class="item w-100 px-3">
                      <div style="width: 100%">
                        <div class="card card-height" style="max-width: 90vw;">
                          <div class="card-body">
                            <div class="d-flex justify-content-between align-items-center mb-1">
                              <div class="d-flex flex-row">
                                <div class="avatar-container me-3">
                                  <div v-if="workersList[slide - 1].logo" class="me-1 rounded" :style="{
                                    backgroundImage: 'url(' + workersList[slide - 1].logo + ')',
                                    backgroundSize: 'cover',
                                    backgroundRepeat: 'no-repeat',
                                    height: '60px',
                                    width: '60px',
                                    backgroundPosition: 'center',
                                  }">
                                  </div>
                                  <div v-else class="avatar avatar-lg me-1" style="background-color: transparent ;">
                                    <image-off :size="70" fillColor="#5e5873" />
                                  </div>
                                  <div v-if="workersList[slide - 1].profile_verified" :class="{
                                    'badge-container-no-image': !workersList[slide - 1].logo,
                                    'badge-container': workersList[slide - 1].logo
                                  }">
                                    <img :src="VITE_ASSET_URL + 'assets/public/assets/img/certif/subscribed-badge.webp'"
                                      class="badge-img ">
                                  </div>
                                </div>

                                <div class="user-info mb-4">
                                  <h5 v-if="workersList[slide - 1].company_name" class="mb-0 text-start limit-text">{{
                                    workersList[slide - 1].company_name ?? ' ' }}</h5>
                                  <p v-if="workersList[slide - 1].specialty"
                                    class="card-text text-start mb-0 specialty">
                                    {{
                                      workersList[slide - 1].specialty ?? ' ' }}</p>
                                  <p v-if="workersList[slide - 1].address.town"
                                    class="card-text text-start mb-0 limit-text">{{ workersList[slide - 1].address.town
                                      ?
                                      workersList[slide - 1].address.town + ` (${workersList[slide -
                                        1].address.zipcode.slice(0,
                                          2)})` : ' ' }}</p>
                                </div>

                              </div>

                            </div>
                            <p class="card-text text-start description">
                              {{ workersList[slide - 1].description }}
                            </p>
                            <p class="text-center">
                              <Link :href="'/annuaire/' + workersList[slide - 1].platform_code"
                                class="btn btn-outline-secondary text-secondary waves-effect on-see-profile-action-btn rounded fw-normal"
                                style="font-size: 14px !important;">
                                <i class="bx bx-user"></i>{{ t('w_act_searchPro_seeProfile') }}
                              </Link>
                              <!-- <Link 
                              :href="'/annuaire/' + workersList[slide-1].platform_code"
                              class="btn btn-outline-secondary text-secondary waves-effect me-2 me-md-3 me-sm-1 on-see-profile-action-btn rounded-1 fw-normal" >
                              <i class="bx bx-user"></i>{{ t('w_act_searchPro_seeProfile') }}
                          </Link> -->
                              <!-- <button
                              type="button" 
                              class="btn btn-outline-primary text-orange waves-effect on-see-profile-action-btn mt-sm-1 my-md-1 my-xl-1 my-sm-1 rounded-1 fw-normal"
                              @click="onContact(workersList[slide-1])">
                              <i class="bx bx-envelope"></i>{{ t('w_act_contactPro') }}
                          </button> -->
                            </p>
                          </div>
                          <div class="card-footer text-white p-0 text-center bg-dark-blue">
                            {{ t('w_txt_wkSearch_topelaCode') }} <strong>{{ workersList[slide - 1].platform_code
                            }}</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Slide>

                <!-- <template #addons>
              <div class="custom-pagination">
                <div v-for="(index) in totalPages" :key="index" class="pagination-dot" :class="{ active: index === currentPage }" @click="changePage(index)"></div>
              </div>
            </template> -->
              </Carousel>
            </div>
            <div v-else class="p-3 my-3 col-md-12">

            </div>
          </div>
          <div v-else style="height: 60vh; display: flex; justify-content: center; align-items: center;">
            <div>
              <vue-loaders-ball-spin-fade-loader color="#FEB23C" scale="1"></vue-loaders-ball-spin-fade-loader>
            </div>
          </div>
          <div class="d-flex justify-content-center mt-4">
            <Link href="/annuaire/search/" class="btn btn-primary rounded px-5 btn-height btn-text-visibility">
              {{ t('w_act_home_pro_section_cta') }}
            </Link>
          </div>
        </div>
      </section>

      <section class="feature-section"
        :style="{ background: 'url(' + VITE_ASSET_URL + 'assets/public/assets/img/homepage/Photo_BtoC.webp)' }">
        <div class="container ">
          <!-- <div class="row">
            <div class="col-md-6">
              <h2 class="mt-md-2 text-white fw-normal h-style h2-style use-poppins"
                v-html="formattedFeatureSectionHeading(t('w_txt_home_feature_section_heading'))"></h2>
            </div>
          </div> -->

          <!-- <div class="mt-3">
            <div class="mb-1">
              <orange-circle-check class="me-2" />
              <span class="text-white sized-text"><b>{{ t('w_txt_home_feature_section_feature1') }}</b></span>
            </div>
            <div class="mb-1">
              <orange-circle-check class="me-2" />
              <span class="text-white sized-text"><b>{{ t('w_txt_home_feature_section_feature2') }}</b></span>
            </div>
            <div class="mb-1">
              <orange-circle-check class="me-2" />
              <span class="text-white sized-text"><b>{{ t('w_txt_home_feature_section_feature3') }}</b></span>
            </div>
            <div class="mb-1">
              <orange-circle-check class="me-2" />
              <span class="text-white sized-text"><b>{{ t('w_txt_home_feature_section_feature4') }}</b></span>
            </div>
            <div class="mb-1">
              <orange-circle-check class="me-2" />
              <span class="text-white sized-text"><b>{{ t('w_txt_home_feature_section_feature5') }}</b></span>
            </div>
            <div class="mb-1">
              <orange-circle-check class="me-2" />
              <span class="text-white sized-text"><b>{{ t('w_txt_home_feature_section_feature6') }}</b></span>
            </div>
          </div> -->


          <div class="row d-flex justify-content-center align-items-center py-5 mx-md-5">
            <h2 class="text-center mt-md-5 mb-5 fw-normal h-style h2-style use-poppins text-white ">
              {{ t('w_txt_home_feature_section_heading_title') }}
            </h2>
            <div class="d-flex justify-content-center align-items-center">
              <Link href="/auth/signup/" class="btn btn-primary rounded px-5 btn-height btn-text-visibility">
                {{ t('w_act_home_feature_section_cta') }}
              </Link>
            </div>

            <nav class="not-visible-content">
              <a href="/">{{ t('w_txt_home_free_client_registration') }}</a>
            </nav>
          </div>

          <!-- <div class="col-md-4 my-5 my-md-0">
              <p class="quote mb-5">
                <i>
                  {{ t('w_txt_home_feature_section_testimonial') }}
                </i>
              </p>
              <p class="quote-author mt-5">
                {{ t('w_txt_home_feature_section_testimonial_author') }}
              </p>
            </div> -->



        </div>

      </section>




      <section class="py-4" style="background-color: #f8f8f8;">
        <CsBlog />
      </section>

      <section class="py-4 py-md-5 bg-orange">
        <div class="container">

          <div class="row align-items-center text-center text-md-start">

            <!-- TEXTE -->
            <div class="col-12 col-md-8 mb-3 mb-md-0">
              <h3 class="fw-bold fs-1 h-style h2-style use-poppins mb-2">
                {{ t('w_txt_home_support_section_heading') }}

              </h3>
              <p class="fs-5 mb-0">
                {{ t('w_txt_home_support_section_paragraph') }}

              </p>
            </div>

            <!-- CTA -->
            <div class="col-12 col-md-4 text-center">
              <a class="btn btn-dark bg-dark-blue rounded px-4 px-md-5 btn-height btn-text-visibility d-inline-block mb-2"
                href="/contact/">
                {{ t('w_act_home_support_section_cta') }}
              </a>
              <p class="mb-0 small">
                {{ t('w_txt_home_support_section_cta_subtext') }}

                <strong>{{ t('w_txt_home_support_section_cta_subtext_value') }}</strong>
              </p>
            </div>

          </div>

        </div>
      </section>





    </DefaultVue>

    <button v-if="hydrated" id="requestPostButton" @click="() => is_modal_visible = true" v-show="false" type="button"
      class="btn btn-primary" data-bs-toggle="modal" data-bs-target="#requestPost">
      Launch demo modal
    </button>

    <ModalComponent v-if="hydrated" id="requestPost" :no-close="true" :remove-padding="true" :use-custom-w-idth="true"
      :add-radius="true" :on-modal-hidden="() => is_modal_visible = false">
      <template v-slot:content>
        <BusinessRequestForm v-if="is_modal_visible" />
        <button id="dismissRequestPostModal" v-show="false" type="button" data-bs-dismiss="modal">dismiss</button>
      </template>
    </ModalComponent>

    <ReinitializePassword />
    <ErrorComponent />
    <ImageUploadLimitModal />
    <ImageUploadDimensionLimitModal />

    <RoseAssistant v-if="hydrated" />
  </div>
</template>

<style scoped>
@import url('@/assets/css/bootstrap-extended.css');
@import url('@/assets/css/animate.css');
@import url('@/assets/css/main.css');
@import url('@/assets/css/responsive.css');

.btn-text-visibility {
  font-size: 20px !important;
  font-weight: normal !important;
}

.free-text {
  font-weight: normal;
  --bs-text-opacity: 1;
  color: rgba(var(--bs-dark-rgb), var(--bs-text-opacity)) !important;
  font-size: 10px !important;
  margin-bottom: 0px !important;
}

.partner-login-hotspot {
  position: absolute;
  top: 140px;
  left: 0;
  z-index: 10;
  width: 100%;
  min-height: 50px;
  background: transparent;
  cursor: pointer;
  font-size: 1.2rem;
}

@media screen and (max-width: 768px) {
  .partner-login-hotspot {
    top: 90px;
  }
}

.quote {
  color: rgb(247, 242, 242);
  font-size: 18px;
  line-height: 1.5;
  font-style: italic;
  text-align: center;
}

.quote-author {
  color: rgb(243, 237, 237);
  font-size: 16px;
  text-align: center;
  margin-top: 10px;
}

html {
  scroll-behavior: smooth !important;
}

.underline {
  text-decoration: underline !important;
}

p {
  color: #333333
}



@media screen and (max-width: 768px) {
  .head-title {
    font-size: 1.5rem !important;
    line-height: 4rem !important;
    width: 90% !important;
  }



}

@media screen and (max-height: 700px) {
  .hero-background {
    height: 100% !important;
    padding-top: 20vh;
  }


}

@media screen and (min-width: 768px) and (max-width: 1003px) {}

@media screen and (min-width: 1103px) {


  .hero-background {
    height: auto;
    /* padding-top: 30vh; */
  }
}

.feature-section {
  width: 100%;
  background-position: center center !important;
  background-repeat: no-repeat !important;
  background-size: cover !important;
  position: relative;
}

.hero-background::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  /* background-color: rgba(0, 0, 0, 0.3); Adjust opacity as needed */
  z-index: 1;
}

.feature-section::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.35);
  /* Adjust opacity as needed */
  z-index: 1;
}

.hero-background * {
  position: relative;
  z-index: 2;
  /* Ensures your text appears above the overlay */
}

.feature-section * {
  position: relative;
  z-index: 2;
  /* Ensures your text appears above the overlay */
}

:deep(.input-width) {
  width: 100%
}

.feature-notice-width {
  position: absolute;
  top: 85vh;
  left: 50%;
  transform: translate(-50%, -40%);
  width: 90vw
}

.illustration-b-to-c_close-up-width {
  width: 75px
}

.sticker {
  position: absolute;
  bottom: -10rem;
  right: -3rem;
  width: 100px
}

/* .list-of-pros {
    margin-top: 50vh !important;
} */

@media (max-height: 768px) {
  .hero-background {
    /* margin-top: 5vh; */
    height: auto;
    /* padding-top: 10vh; */
  }

  .feature-notice-width {
    top: 95vh;
  }

  /* .list-of-pros {
    margin-top: 70vh !important;
  } */
}

@media only screen and (min-width: 768px) {


  :deep(.input-width) {
    width: 30rem !important;
  }

  .feature-notice-width {
    transform: translate(-50%, -50%);
    width: 75%;
    top: 100%;
  }

  .hero-background {
    /* margin-top: 5vh; */
    height: 60vh;
    padding-top: 20vh;
  }

  .sticker {
    position: absolute;
    top: -6.5rem;
    right: -6.5rem;
    width: 150px
  }

  /* .list-of-pros {
    margin-top: 3rem !important;
  } */

  .idmat-illustration {
    bottom: -0.7rem;
    left: 1%;
    width: 120px
  }

  .pauline-illustration {
    bottom: -0.7rem;
    right: 1%;
    width: 140px
  }
}

@media only screen and (min-width: 992px) {
  .illustration-b-to-c_close-up-width {
    width: 150px
  }
}

@media only screen and (min-width: 1600px) {
  .idmat-illustration {
    bottom: -0.7rem;
    left: 13%;
    width: 200px
  }

  .pauline-illustration {
    bottom: -0.7rem;
    right: 13%;
    width: 220px
  }

  .feature-notice-width {
    transform: translate(-50%, -50%);
    width: 55%;
    top: 100%;
  }
}

@media screen and (max-width: 768px) {
  .feature-section .sized-text {
    font-size: 14px !important;
  }
}


.request-post-form {
  padding: 0.75rem;
}

#searchPros:hover,
#discover:hover {
  color: white !important;
}

.custom-carousel {
  padding: 0px;
}

.custom-pagination {
  display: flex;
  justify-content: center;
}

.pagination-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin: 0 5px;
  background: lightgray;
  /* Default color for inactive slides */
}

.pagination-dot.active {
  background: #3c9cfd;
  ;
  /* Blue color for active slide */
}

.on-see-profile-action-btn-modal {
  padding: 10px 15px !important;
  font-size: 14px !important;
  width: 180px !important;
}

.description {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  line-clamp: 4;
  /* Number of lines to show */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 6rem;
}

.specialty {
  width: 200px;
  /* Set the width of the container */
  white-space: nowrap;
  /* Prevent text from wrapping */
  overflow: hidden;
  /* Hide overflowing content */
  text-overflow: ellipsis;
  /* Add ellipsis when text is truncated */
}

.user-info {
  height: 5rem;
}

.avatar-container {
  position: relative;
}

.badge-container {
  position: relative;
  bottom: -10px;
}

.badge-container-no-image {
  position: relative;
  left: -8px;
  bottom: 0px;
}

.badge-img {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 20px;
  height: auto;
}

.limit-text {
  width: 240px !important;
  /* Set the width of the container */
  white-space: nowrap;
  /* Prevent text from wrapping */
  overflow: hidden;
  /* Hide overflowing content */
  text-overflow: ellipsis;
  /* Add ellipsis when text is truncated */
}

.btn-height {
  min-height: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
}

.btn {
  font-size: 16px;
}

.highlight-orange {
  color: #F0A43D;
  font-family: Arial, Helvetica, sans-serif;
  /* font-weight: 900;
  font-size: 4rem; */
  transition: color 0.5s ease-in-out;

}




.highlight-script {
  /* font-family: 'Protest Riot', cursive; */
  font-style: italic;
  font-size: larger;
  font-weight: 900;
  position: relative;
  display: inline-block;
  padding-bottom: 5px;
  color: #F0A43D;
}


.highlight-script::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 8px;
  border-bottom: 2px solid white;
  border-radius: 50%/100%;
  transform: scaleX(1.1);
  opacity: 0.5;
}

.advantage-item {
  text-align: left;
  border-left: 5px solid #F0A43D;
  padding-left: 1rem;
}

.advantage-item p {
  font-size: 16px;
  color: #233648;
  font-weight: 600;
}

.link-hover-blue:hover {
  color: #233648 !important;
}

.step-badge {
  width: 80px;
  height: 80px;
  font-size: 50px;
  line-height: 80px;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  justify-content: center;
  align-items: center;

}

.pro-feature-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: 1px solid #233648;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  color: #233648;
}

.pro-feature-icon i {
  font-size: 50px;

}

.partner-logo {
  height: 150px;
  filter: grayscale(100%);
  opacity: 0.7;
  transition: filter 0.3s ease, opacity 0.3s ease, transform 0.3s ease;
}

/* Hover desktop */
.partner-logo:hover {
  filter: grayscale(0%);
  opacity: 1;
  transform: scale(1.05);
}

/* Mobile : logos plus petits */
@media (max-width: 768px) {
  .partner-logo {
    height: 75px;
  }
}

.bg-dark-blue {
  background-color: #233648 !important;
}

.bg-orange {
  background-color: #F0A43D !important;
}
</style>
